import React, {ReactElement, useRef, useState} from 'react';
import './DragAndDropStyle.scss';
import {IconButton, Tooltip} from '@material-ui/core';
import {useSnackbar} from 'notistack';
import {useTranslation} from 'react-i18next';
import {SnackBarConfig} from '../../../utils/SnackBarConfig';
import {CancelOutlined} from '@material-ui/icons';

interface DragAndDropProps {
  title: string | ReactElement;
  extensionsSubTitles?: string;
  multiple?: boolean;
  showSelectedFile?: boolean;
  handleFileChange?(files: any): void;
  acceptFileTypes?: string;
  className?: string;
}

const DragAndDrop = (props: DragAndDropProps): ReactElement => {
  const {
    title,
    extensionsSubTitles,
    multiple,
    handleFileChange,
    showSelectedFile,
    acceptFileTypes,
    className = ''
  } = props;
  const [uploadFile, setUploadFile]: any = useState([]);
  const [dragOverActive, setDragOver]: any = useState(false);
  const {enqueueSnackbar} = useSnackbar();
  const {t} = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const getInputFile = (event: any): void => {
    event.preventDefault();

    if (event?.target?.files?.length > 0) {
      let ind = 0;
      const arrFiles = [];
      while (ind < event.target.files?.length) {
        arrFiles.push(event.target.files[ind]);
        ind++;
      }
      setUploadFile(arrFiles);
      if (typeof handleFileChange === 'function') {
        handleFileChange(arrFiles);
      }
    }
  };

  const dragEnter = (e: any) => {
    setDragOver(true);
    e.preventDefault();
  };

  const dragOver = (e: any) => {
    e.preventDefault();
  };

  const dragLeave = (e: any) => {
    setDragOver(false);
    e.preventDefault();
  };

  const fileDrop = (e: any) => {
    setDragOver(false);
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files?.length > 1 && !multiple) {
      enqueueSnackbar(t('COMMON.NO_MULTIPLE_FILES'), SnackBarConfig('e'));
      return;
    }
    let ind = 0;
    const arrFiles = [];
    while (ind < files?.length) {
      arrFiles.push(files[ind]);
      ind++;
    }
    setUploadFile(arrFiles);
    if (typeof handleFileChange === 'function') {
      handleFileChange(arrFiles);
    }
  };

  const removeFile = (index: number): void => {
    const arrFiles = [...uploadFile];
    if (index >= 0 && Array.isArray(arrFiles) && arrFiles.length > 0) {
      arrFiles.splice(index, 1);
      setUploadFile(arrFiles);
    }
    if (!multiple && fileInputRef?.current?.value) {
      fileInputRef.current.value = '';
    }
    if (typeof handleFileChange === 'function') {
      handleFileChange(arrFiles);
    }
  };

  const renderSelectedFiles = (): React.ReactElement => {
    if (
      showSelectedFile &&
      Array.isArray(uploadFile) &&
      uploadFile.length > 0
    ) {
      return (
        <>
          {uploadFile.map((item: any, i: number) => (
            <div className="close-file-container" key={i}>
              <div className="fine-name-container">{item?.name}</div>
              <div
                className="close-icon-container"
                onClick={() => removeFile(i)}
                data-test-id="pcl-appointments-close">
                <Tooltip title="Close">
                  <IconButton>
                    <CancelOutlined />
                  </IconButton>
                </Tooltip>
              </div>
            </div>
          ))}
        </>
      );
    } else {
      return <div />;
    }
  };

  const fileInputClicked = (): void => {
    if (fileInputRef?.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <>
      <div
        onDragOver={dragOver}
        onDragEnter={dragEnter}
        onDragLeave={dragLeave}
        onDrop={fileDrop}
        onClick={fileInputClicked}
        className={`drop-container ${
          dragOverActive ? 'drop-container-drag-over' : ''
        } ${className}`}>
        {typeof title === 'string' ? (
          <label className="drop-title">{title}</label>
        ) : (
          title
        )}
        <input
          ref={fileInputRef}
          id="icon-button-file"
          type="file"
          className="file-input"
          onChange={getInputFile}
          multiple={!!multiple}
          accept={acceptFileTypes ?? ''}
        />
        <div className="placeholder-text">{extensionsSubTitles}</div>
      </div>
      {renderSelectedFiles()}
    </>
  );
};
export default DragAndDrop;
